import { 
    defineComponent, 
    defineAsyncComponent, 
    ref, 
    reactive, 
    toRef, 
    watch,
    computed
} from 'vue'
import { BlockModule } from '../common'
import { scrollToEl } from '@/lib/scrolling'
import { useBreakpoints, useUrlSearchParams } from '@vueuse/core'
import { tailwindBreakpoints } from '../tailwind'
import { useHighlightsListingAsync } from '@/datasources'

new BlockModule({
    selector: '.block-highlights-listing',
    vueComponent: defineComponent({
        name: 'block-highlights-listing',
        components: {
            ToggleGroupRoot: defineAsyncComponent(
                () => import('radix-vue').then((x) => x.ToggleGroupRoot)
            ),
            ToggleGroupItem: defineAsyncComponent(
                () => import('radix-vue').then((x) => x.ToggleGroupItem)
            ),
            LazyImage: defineAsyncComponent(
                () => import('@/components/website/lazy-image/lazy-image.vue')
            ),
            AutocompleteControl: defineAsyncComponent(
                () => import('@/components/website/forms/autocomplete-control/AutocompleteControl.vue')
            ),
            Pagination: defineAsyncComponent(
                () => import('@/components/website/pagination/Pagination.vue')
            ),
            FadeInOut: defineAsyncComponent(
                () => import('@/components/website/transition/FadeInOut.vue')
            ),
            MsigBadge: defineAsyncComponent(
                () => import('@/components/ui/badge/Badge.vue')
            )
        },
        props: {
            defaultSort: { type: Number, default: 3 }, // AtoZ=0,ZtoA=1,OldtoNew=2,NewtoOld=3
            pageSize: { type: Number, default: 24 },
            filterTabs: String,
            highlightsBaseUrl: String
        },
        setup(props) {
            const params = useUrlSearchParams('history')
            const categoriesParam = params.categories as string ?? ''
            const pageNumberParam = params.pageNumber as string ?? 1

            const filterTabs = props.filterTabs ? JSON.parse(props.filterTabs) as { key: string; name: string }[] : []

            // Map `contentTypeKeys` to filter names using `props.filterTabs`
            const filterKeyToNameMap = computed(() => {
                const map: Record<string, string> = {}
                filterTabs.forEach((tab: { key: string; name: string }) => {
                    map[tab.key] = tab.name
                })
                console.log('filterKeyToNameMap', map)
                return map
            })

            const contentTagKeys = computed(() => {
                return categoriesParam.split(',')
                    .map(name =>
                        Object.keys(filterKeyToNameMap.value).find(
                            key => filterKeyToNameMap.value[key] === name,
                        ),
                    )
                    .filter(Boolean) as string[]
            })

            const isBusy = ref(true)
            const highlightsFilterRef = ref<HTMLElement|null>(null)

            const breakpoints = useBreakpoints(tailwindBreakpoints)
            const mdAndGreater = breakpoints.greaterOrEqual('md')

            const filters = reactive({
                // contentTags: ['All'] as string[],
                contentTags: contentTagKeys.value.length > 0 ? contentTagKeys.value : ['All'],
                pageNumber: parseInt(pageNumberParam),
                mode: props.defaultSort,
            })

            const highlightListingAsync = useHighlightsListingAsync(
                toRef(filters, 'pageNumber'),
                props.pageSize,
                toRef(filters, 'mode'),
                toRef(filters, 'contentTags'),
                { evaluating: isBusy }
            )

            const updateUrlParams = () => {
                const searchParams = new URLSearchParams()
                if (filters.contentTags.length > 0) {
                    const filterNames = filters.contentTags
                        .map(key => filterKeyToNameMap.value[key])
                        .filter(Boolean)
                        .join(',');
                    console.log(filterNames)
                    searchParams.set('categories', filterNames)
                }

                searchParams.set("pageNumber", filters.pageNumber.toString())
                // Update the browser's URL without reloading the page
                const newUrl = `${props.highlightsBaseUrl}?${searchParams.toString()}`
                window.history.replaceState(null, "", newUrl)
            }

            watch(
                () => filters.contentTags,
                (newContentTags) => {
                    filters.pageNumber = 1
                    if (newContentTags.length > 1 && newContentTags.findIndex(x => x === 'All') === newContentTags.length - 1) {
                        filters.contentTags = ['All']
                    }  else if (newContentTags.length > 1 && newContentTags.includes('All')) {
                        filters.contentTags = newContentTags.filter(x => x !== 'All')
                    } else if (newContentTags.length === 0) {
                        filters.contentTags = ['All']
                    }
                    updateUrlParams()
                }
            )

            watch(() => filters.pageNumber, (newPageNum) => {
                updateUrlParams()
                scrollToEl(highlightsFilterRef.value, 100)
            })

            return {
                isBusy,
                highlightsFilterRef,
                highlightListingAsync,
                filters,
                mdAndGreater,
            }
        },
    }),
})
